import {
  useId,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { LiksFormFieldLabel } from '../LiksFormFieldLabel/LiksFormFieldLabel';
import './LiksTextFormField.css';

type SharedFieldProps = {
  label: ReactNode;
  value: string;
  onChange?: (value: string) => void;
  required?: boolean;
  error?: string | readonly string[] | null;
  className?: string;
  nested?: boolean;
  id?: string;
};

type InputFieldProps = SharedFieldProps & {
  multiline?: false;
  trailing?: ReactNode;
} & Omit<
    ComponentPropsWithoutRef<'input'>,
    | 'value'
    | 'onChange'
    | 'id'
    | 'className'
    | 'aria-invalid'
    | 'aria-describedby'
  >;

type TextAreaFieldProps = SharedFieldProps & {
  multiline: true;
} & Omit<
    ComponentPropsWithoutRef<'textarea'>,
    | 'value'
    | 'onChange'
    | 'id'
    | 'className'
    | 'aria-invalid'
    | 'aria-describedby'
  >;

export type LiksTextFormFieldProps = InputFieldProps | TextAreaFieldProps;

const SHARED_PROP_KEYS = [
  'label',
  'value',
  'onChange',
  'required',
  'error',
  'className',
  'nested',
  'id',
  'multiline',
  'trailing',
] as const;

function omitSharedProps<T extends object>(
  props: T,
): Omit<T, (typeof SHARED_PROP_KEYS)[number]> {
  const result = { ...props };
  for (const key of SHARED_PROP_KEYS) {
    delete result[key as keyof T];
  }
  return result;
}

export function LiksTextFormField(props: LiksTextFormFieldProps) {
  const {
    label,
    value,
    onChange,
    required = false,
    error,
    className,
    nested = false,
    id: idProp,
  } = props;
  const generatedId = useId();
  const fieldId = idProp ?? generatedId;
  const errorId = `${fieldId}-error`;
  const errorMessages =
    error == null
      ? []
      : Array.isArray(error)
        ? error.filter((message) => message !== '')
        : error === ''
          ? []
          : [error];
  const hasError = errorMessages.length > 0;
  const handleChange =
    onChange != null
      ? (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          onChange(event.target.value)
      : undefined;
  const controlClassName = [
    'liks-text-form-field__input',
    props.multiline && 'liks-text-form-field__input--textarea',
    hasError && 'liks-text-form-field__input--invalid',
  ]
    .filter(Boolean)
    .join(' ');

  let control: ReactNode;
  if (props.multiline) {
    const { rows = 4, ...textareaProps } = omitSharedProps(props);
    control = (
      <textarea
        {...textareaProps}
        id={fieldId}
        className={controlClassName}
        value={value}
        onChange={handleChange}
        required={required}
        rows={rows}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
      />
    );
  } else {
    const { trailing } = props;
    const { type = 'text', ...inputProps } = omitSharedProps(props);
    const input = (
      <input
        {...inputProps}
        id={fieldId}
        type={type}
        className={controlClassName}
        value={value}
        onChange={handleChange}
        required={required}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
      />
    );
    control =
      trailing != null ? (
        <div
          className={[
            'liks-text-form-field__input-wrapper',
            hasError && 'liks-text-form-field__input-wrapper--invalid',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {input}
          <div className="liks-text-form-field__trailing">{trailing}</div>
        </div>
      ) : (
        input
      );
  }

  return (
    <div
      className={[
        'liks-text-form-field',
        nested && 'liks-text-form-field--nested',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <LiksFormFieldLabel htmlFor={fieldId} required={required}>
        {label}
      </LiksFormFieldLabel>
      {control}
      {hasError &&
        (errorMessages.length === 1 ? (
          <p id={errorId} className="liks-text-form-field__error" role="alert">
            {errorMessages[0]}
          </p>
        ) : (
          <ul
            id={errorId}
            className="liks-text-form-field__error-list"
            role="alert"
          >
            {errorMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ))}
    </div>
  );
}
